import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import {
    Box, Container, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TablePagination, Button,
    Chip, IconButton, TextField, InputAdornment, Card, CardContent,
    Grid, Tooltip, Snackbar, Alert, Select, MenuItem, FormControl,
    InputLabel, Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress,
} from '@mui/material';
import {
    Search as SearchIcon,
    LinkedIn as LinkedInIcon,
    ContentCopy as CopyIcon,
    ArrowBack as ArrowBackIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    Send as SendIcon,
    PersonSearch as PersonSearchIcon,
    Business as BusinessIcon,
    OpenInNew as OpenInNewIcon,
    SkipNext as SkipIcon,
    Done as DoneIcon,
    Reply as ReplyIcon,
} from '@mui/icons-material';

const statusConfig = {
    ready: { label: 'Ready', color: '#3b82f6', bg: '#eff6ff' },
    sent: { label: 'Sent', color: '#f59e0b', bg: '#fffbeb' },
    connected: { label: 'Connected', color: '#10b981', bg: '#ecfdf5' },
    replied: { label: 'Replied', color: '#8b5cf6', bg: '#f5f3ff' },
    skipped: { label: 'Skipped', color: '#6b7280', bg: '#f3f4f6' },
};

export default function LinkedInOutreachPage() {
    const navigate = useNavigate();
    const [outreach, setOutreach] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ ready: 0, sent: 0, connected: 0, replied: 0, skipped: 0, total: 0 });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [enrichDialog, setEnrichDialog] = useState(false);
    const [enriching, setEnriching] = useState(false);
    const [enrichLimit, setEnrichLimit] = useState(50);

    const fetchStats = useCallback(async () => {
        const { data, error } = await supabase
            .from('linkedin_outreach')
            .select('status');

        if (!error && data) {
            const counts = { ready: 0, sent: 0, connected: 0, replied: 0, skipped: 0, total: data.length };
            data.forEach(r => { counts[r.status] = (counts[r.status] || 0) + 1; });
            setStats(counts);
        }
    }, []);

    const fetchOutreach = useCallback(async () => {
        setLoading(true);
        try {
            let query = supabase
                .from('linkedin_outreach')
                .select(`
          *,
          leads:lead_id (
            website_domain, lead_company_name, industry, 
            linkedin_company_url, website_url
          )
        `, { count: 'exact' })
                .order('created_at', { ascending: false })
                .range(page * rowsPerPage, (page + 1) * rowsPerPage - 1);

            if (statusFilter !== 'all') {
                query = query.eq('status', statusFilter);
            }

            if (searchQuery) {
                query = query.or(
                    `person_name.ilike.%${searchQuery}%,company_name.ilike.%${searchQuery}%,person_title.ilike.%${searchQuery}%`
                );
            }

            const { data, error, count } = await query;

            if (error) throw error;
            setOutreach(data || []);
            setTotalCount(count || 0);
        } catch (err) {
            console.error('Error fetching outreach:', err);
            setSnackbar({ open: true, message: 'Failed to load outreach data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, statusFilter, searchQuery]);

    useEffect(() => {
        fetchOutreach();
        fetchStats();
    }, [fetchOutreach, fetchStats]);

    const handleStatusChange = async (id, newStatus) => {
        const timestamps = {};
        if (newStatus === 'sent') timestamps.sent_at = new Date().toISOString();
        if (newStatus === 'connected') timestamps.connected_at = new Date().toISOString();
        if (newStatus === 'replied') timestamps.replied_at = new Date().toISOString();

        const { error } = await supabase
            .from('linkedin_outreach')
            .update({ status: newStatus, ...timestamps })
            .eq('id', id);

        if (error) {
            setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
        } else {
            setSnackbar({ open: true, message: `Status updated to ${newStatus}`, severity: 'success' });
            fetchOutreach();
            fetchStats();
        }
    };

    const handleCopyMessage = (message, personName) => {
        if (!message) {
            setSnackbar({ open: true, message: 'No message to copy', severity: 'warning' });
            return;
        }
        navigator.clipboard.writeText(message);
        setSnackbar({ open: true, message: `Message for ${personName} copied!`, severity: 'success' });
    };

    const handleEnrich = async () => {
        setEnriching(true);
        try {
            const response = await fetch('/api/linkedin/enrich', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ limit: enrichLimit }),
            });
            const data = await response.json();
            if (data.error) throw new Error(data.error);
            setSnackbar({
                open: true,
                message: `Enriched ${data.saved || 0} leads (${data.found || 0} decision-makers found)`,
                severity: 'success',
            });
            setEnrichDialog(false);
            fetchOutreach();
            fetchStats();
        } catch (err) {
            setSnackbar({ open: true, message: `Enrichment failed: ${err.message}`, severity: 'error' });
        } finally {
            setEnriching(false);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate('/leads')} sx={{ color: '#64748b' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkedInIcon sx={{ fontSize: 36, color: '#0077b5' }} />
                        LinkedIn Outreach
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                        Find decision-makers, copy connection messages, and track your LinkedIn outreach
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => { fetchOutreach(); fetchStats(); }}
                    sx={{ borderColor: '#e2e8f0', color: '#475569' }}
                >
                    Refresh
                </Button>
                <Button
                    variant="contained"
                    startIcon={<PersonSearchIcon />}
                    onClick={() => setEnrichDialog(true)}
                    sx={{
                        background: 'linear-gradient(135deg, #0077b5 0%, #005885 100%)',
                        '&:hover': { background: 'linear-gradient(135deg, #005885 0%, #004060 100%)' },
                    }}
                >
                    Enrich More Leads
                </Button>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {Object.entries(statusConfig).map(([key, config]) => (
                    <Grid item xs={6} sm={4} md={2.4} key={key}>
                        <Card
                            sx={{
                                cursor: 'pointer',
                                border: statusFilter === key ? `2px solid ${config.color}` : '1px solid #e2e8f0',
                                boxShadow: statusFilter === key ? `0 0 0 1px ${config.color}20` : 'none',
                                transition: 'all 0.2s',
                                '&:hover': { borderColor: config.color, transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
                            }}
                            onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
                        >
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', fontSize: 11 }}>
                                    {config.label}
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: config.color, mt: 0.5 }}>
                                    {stats[key] || 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Search & Filters */}
            <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                    size="small"
                    placeholder="Search by name, title, or company..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                    }}
                    sx={{ flex: 1, maxWidth: 400 }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                    >
                        <MenuItem value="all">All ({stats.total})</MenuItem>
                        {Object.entries(statusConfig).map(([key, config]) => (
                            <MenuItem key={key} value={key}>
                                {config.label} ({stats[key] || 0})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body2" sx={{ color: '#94a3b8', ml: 'auto' }}>
                    {totalCount} results
                </Typography>
            </Paper>

            {/* Table */}
            <TableContainer component={Paper} sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                            <TableCell sx={{ fontWeight: 700, color: '#475569', width: 200 }}>Person</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#475569', width: 160 }}>Title</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#475569', width: 200 }}>Company</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#475569', width: 100 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#475569', width: 120 }}>LinkedIn</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Message</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#475569', width: 180 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                    <CircularProgress size={32} />
                                </TableCell>
                            </TableRow>
                        ) : outreach.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                    <Typography color="text.secondary">
                                        {statusFilter !== 'all'
                                            ? `No ${statusFilter} outreach items found`
                                            : 'No outreach data yet. Click "Enrich More Leads" to get started.'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : outreach.map((row) => {
                            const sc = statusConfig[row.status] || statusConfig.ready;
                            return (
                                <TableRow
                                    key={row.id}
                                    sx={{
                                        '&:hover': { backgroundColor: '#f8fafc' },
                                        opacity: row.status === 'skipped' ? 0.6 : 1,
                                    }}
                                >
                                    {/* Person */}
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                            {row.person_name}
                                        </Typography>
                                    </TableCell>

                                    {/* Title */}
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: '#475569', fontSize: 13 }}>
                                            {row.person_title}
                                        </Typography>
                                    </TableCell>

                                    {/* Company */}
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <BusinessIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                            <Typography variant="body2" sx={{ color: '#475569', fontSize: 13 }}>
                                                {row.company_name || row.leads?.lead_company_name || '—'}
                                            </Typography>
                                        </Box>
                                        {row.leads?.website_domain && (
                                            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 11 }}>
                                                {row.leads.website_domain}
                                            </Typography>
                                        )}
                                    </TableCell>

                                    {/* Status */}
                                    <TableCell>
                                        <Chip
                                            label={sc.label}
                                            size="small"
                                            sx={{
                                                backgroundColor: sc.bg,
                                                color: sc.color,
                                                fontWeight: 600,
                                                fontSize: 12,
                                                border: `1px solid ${sc.color}30`,
                                            }}
                                        />
                                    </TableCell>

                                    {/* LinkedIn Search */}
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {/* Company button — opens company LinkedIn page */}
                                            {(row.company_linkedin_url || row.leads?.linkedin_company_url) && (() => {
                                                const compUrl = row.company_linkedin_url || row.leads?.linkedin_company_url || '';
                                                const fullUrl = compUrl.startsWith('http') ? compUrl : `https://${compUrl}`;
                                                return (
                                                    <Tooltip title="Company LinkedIn page">
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            href={fullUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            startIcon={<BusinessIcon />}
                                                            sx={{
                                                                borderColor: '#94a3b830',
                                                                color: '#475569',
                                                                fontSize: 11,
                                                                textTransform: 'none',
                                                                minWidth: 0,
                                                                px: 1,
                                                                '&:hover': { borderColor: '#475569', backgroundColor: '#47556908' },
                                                            }}
                                                        >
                                                            Company
                                                        </Button>
                                                    </Tooltip>
                                                );
                                            })()}
                                            {/* Person button — searches person name on company people page */}
                                            {row.person_linkedin_url && row.person_name && row.person_name !== '—' && (
                                                <Tooltip title={`Search "${row.person_name}" on LinkedIn`}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        href={row.person_linkedin_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        startIcon={<PersonSearchIcon />}
                                                        sx={{
                                                            borderColor: '#0077b520',
                                                            color: '#0077b5',
                                                            fontSize: 11,
                                                            textTransform: 'none',
                                                            minWidth: 0,
                                                            px: 1,
                                                            '&:hover': { borderColor: '#0077b5', backgroundColor: '#0077b508' },
                                                        }}
                                                    >
                                                        Person
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>

                                    {/* Message Preview */}
                                    <TableCell>
                                        {row.connection_message ? (
                                            <Tooltip title={row.connection_message}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#64748b',
                                                        fontSize: 12,
                                                        maxWidth: 200,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    {row.connection_message}
                                                </Typography>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="body2" sx={{ color: '#cbd5e1', fontSize: 12, fontStyle: 'italic' }}>
                                                No message yet
                                            </Typography>
                                        )}
                                    </TableCell>

                                    {/* Actions */}
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                            {row.connection_message && (
                                                <Tooltip title="Copy message">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleCopyMessage(row.connection_message, row.person_name)}
                                                        sx={{ color: '#64748b' }}
                                                    >
                                                        <CopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {row.status === 'ready' && (
                                                <>
                                                    <Tooltip title="Mark as Sent">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleStatusChange(row.id, 'sent')}
                                                            sx={{ color: '#f59e0b' }}
                                                        >
                                                            <SendIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Skip">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleStatusChange(row.id, 'skipped')}
                                                            sx={{ color: '#94a3b8' }}
                                                        >
                                                            <SkipIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}

                                            {row.status === 'sent' && (
                                                <Tooltip title="Mark as Connected">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleStatusChange(row.id, 'connected')}
                                                        sx={{ color: '#10b981' }}
                                                    >
                                                        <CheckCircleIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {row.status === 'connected' && (
                                                <Tooltip title="Mark as Replied">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleStatusChange(row.id, 'replied')}
                                                        sx={{ color: '#8b5cf6' }}
                                                    >
                                                        <ReplyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </TableContainer>

            {/* Enrich Dialog */}
            <Dialog open={enrichDialog} onClose={() => setEnrichDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    <PersonSearchIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#0077b5' }} />
                    Enrich More Leads
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                        This will scan your leads database, find decision-makers from BuiltWith data,
                        and create LinkedIn search entries for them.
                    </Typography>
                    <TextField
                        fullWidth
                        type="number"
                        label="Number of leads to process"
                        value={enrichLimit}
                        onChange={(e) => setEnrichLimit(Math.max(1, parseInt(e.target.value) || 10))}
                        inputProps={{ min: 1, max: 200 }}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEnrichDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleEnrich}
                        disabled={enriching}
                        sx={{ background: 'linear-gradient(135deg, #0077b5 0%, #005885 100%)' }}
                    >
                        {enriching ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Start Enrichment'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}
